import { DESCENDING, SortDirections } from 'constants/sortDirections';
import { formatNumberGivenValueType } from 'modules/productAnalytics/utils';
import { PERCENTAGE } from 'ui/card/constants';

export const sortHeatmapRows = (
  objectArray: any[] = [],
  sortKey = '',
  sortDirection: SortDirections = DESCENDING,
) => {
  const arrCopy = objectArray.slice();
  arrCopy.sort((val1, val2) => {
    let formattedValue1 = val1[sortKey];
    let formattedValue2 = val2[sortKey];
    if (sortKey === 'text') {
      // If we're sorting by text(linkContent) do the following:
      //  - If it's [dynamic] sort it to the bottom, no matter what
      //  - If descending, images go first, text after sorted in reverse alphabetical order
      //  - If ascending, text first (sorted alphabetically), then images
      const toggleInt = sortDirection === DESCENDING ? 1 : -1;
      const imgVal1 = val1.img;
      const imgVal2 = val2.img;
      if (
        typeof formattedValue1 !== 'string' &&
        !imgVal1 &&
        typeof formattedValue2 !== 'string' &&
        !imgVal2
      ) {
        return 0;
      }
      if (typeof formattedValue1 !== 'string' && !imgVal1) {
        return 1;
      }
      if (typeof formattedValue2 !== 'string' && !imgVal2) {
        return -1;
      }
      if (imgVal1 && !imgVal2) {
        return -1 * toggleInt;
      } else if (imgVal2) {
        return toggleInt;
      }

      formattedValue1 = formattedValue1.toLocaleLowerCase();
      formattedValue2 = formattedValue2.toLocaleLowerCase();
    } else if (typeof formattedValue1 === 'string' && typeof formattedValue2 === 'string') {
      formattedValue1 = formattedValue1.toLowerCase();
      formattedValue2 = formattedValue2.toLowerCase();
    }
    if (sortDirection === DESCENDING) {
      return formattedValue1 > formattedValue2 ? -1 : 1;
    }
    return formattedValue2 > formattedValue1 ? -1 : 1;
  });
  return arrCopy;
};

export const sanitizeCSV = (rows: any) => {
  return rows.reduce((acc: any, curr: any) => {
    const {
      hrefIndex,
      id,
      trackingId,
      x,
      y,
      clickRate,
      uniqueClickRate,
      contentId,
      text,
      img,
      ...cleanObj
    } = curr;
    const trackContentId = trackingId?.split('-')?.[0];
    const isSnippet = trackContentId !== contentId?.toString();
    let linkContent = text || img;
    if (isSnippet) {
      const snippetUrl = `${window.location.host}/templates/snippet/${trackContentId}/edit`;
      linkContent =
        linkContent?.length > 0 ? `${linkContent}\n${snippetUrl}` : `[dynamic]\n${snippetUrl}`;
    }
    return [
      ...acc,
      {
        linkContent,
        clickRate: formatNumberGivenValueType(PERCENTAGE, uniqueClickRate),
        ...cleanObj,
      },
    ];
  }, []);
};

export const getTableHeaderLabels = (isAnalyticsBotFilteringEnabled: boolean) => {
  return {
    clickRate: isAnalyticsBotFilteringEnabled ? 'Click rate (filtered)' : 'Click rate',
    totalClicks: isAnalyticsBotFilteringEnabled ? 'Total clicks (filtered)' : 'Total clicks',
    uniqueClicks: isAnalyticsBotFilteringEnabled ? 'Unique clicks (filtered)' : 'Unique clicks',
  };
};

export const getTableHeaderTooltipLabels = (isAnalyticsBotFilteringEnabled: boolean) => {
  return {
    clickRate: isAnalyticsBotFilteringEnabled
      ? 'Unique clicks (filtered)/total emails delivered'
      : 'Unique email clicks/total emails delivered',
    totalClicks: isAnalyticsBotFilteringEnabled
      ? 'Total clicks (filtered) of the URL'
      : 'Total clicks of the URL',
    uniqueClicks: isAnalyticsBotFilteringEnabled
      ? 'Unique clicks (filtered) by user email or ID'
      : 'Number of unique users (by email or user ID) who clicked the URL',
  };
};

export const getCsvTableHeaders = (isAnalyticsBotFilteringEnabled: boolean) => {
  const { clickRate, totalClicks, uniqueClicks } = getTableHeaderLabels(
    isAnalyticsBotFilteringEnabled,
  );
  return [
    { key: 'campaignId', label: 'Campaign ID' },
    { key: 'url', label: 'URL' },
    { key: 'linkContent', label: 'Link content' },
    { key: 'clickRate', label: clickRate },
    { key: 'totalCount', label: totalClicks },
    { key: 'uniqueCount', label: uniqueClicks },
  ];
};

export const getCsvRawTableHeaders = (isAnalyticsBotFilteringEnabled: boolean) => {
  const { clickRate, totalClicks, uniqueClicks } = getTableHeaderLabels(
    isAnalyticsBotFilteringEnabled,
  );
  return [
    { key: 'campaignId', label: 'Campaign ID' },
    { key: 'url', label: 'URL' },
    { key: 'clickRate', label: clickRate },
    { key: 'totalCount', label: totalClicks },
    { key: 'uniqueCount', label: uniqueClicks },
  ];
};
